import Anthropic from "@anthropic-ai/sdk";
import Groq from "groq-sdk";
import { GoogleGenerativeAI, SchemaType, FunctionDeclarationSchemaProperty } from "@google/generative-ai";
import OpenAI from "openai";
import { jsonrepair } from 'jsonrepair';
import { llmUsageLogger, LLMUsageEntry } from './utils/llm-usage-logger';
import { userPromptErrorLogger } from './utils/user-prompt-error-logger';
import { stripLoneSurrogates, safeTruncate } from './utils/surrogate';

// System prompt can be either a simple string or an array of content blocks for caching
type SystemPrompt = string | Anthropic.Messages.TextBlockParam[];

/**
 * Thrown when an in-flight LLM call is cancelled via an AbortSignal (user hit
 * "Stop" in the chat). Distinct from a real error so callers can treat it as a
 * clean stop — no error UI, no fail-open to another provider, no retry.
 */
export class RequestAbortedError extends Error {
    constructor(message = 'Request aborted by user') {
        super(message);
        this.name = 'RequestAbortedError';
    }
}

/**
 * True for any flavour of "the caller aborted" — our own RequestAbortedError,
 * the Anthropic SDK's APIUserAbortError, or a DOMException/AbortError raised by
 * the underlying fetch when the signal fires mid-stream.
 */
export function isAbortError(err: unknown): boolean {
    if (err instanceof RequestAbortedError) return true;
    if (err instanceof Error) {
        return (
            err.name === 'AbortError' ||
            err.name === 'APIUserAbortError' ||
            /aborted|abort(ed)? by user/i.test(err.message)
        );
    }
    return false;
}

interface LLMMessages {
    sys: SystemPrompt;
    user: string;
    prefill?: string;  // Optional assistant prefill to force specific output format (e.g., "{" for JSON)
}

interface LLMOptions {
    model?: string;
    maxTokens?: number;
    temperature?: number;
    topP?: number;
    apiKey?: string;
    baseURL?: string;  // Custom endpoint (e.g. OpenRouter). When unset, the provider's official URL is used.
    partial?: (chunk: string) => void;  // Callback for each chunk
    /**
     * Per-request cancellation. When the caller aborts this signal (user hit
     * "Stop"), the underlying provider request is cancelled and the call throws
     * a RequestAbortedError. Threaded into the provider `messages.create` request
     * options and checked between tool-loop iterations. Currently honored on the
     * Anthropic path (the agent flow's default provider).
     */
    signal?: AbortSignal;
    /**
     * Forces a tool call on the FIRST iteration of streamWithTools only
     * (subsequent iterations revert to auto). Used by fork mode to stop the
     * model from ending its turn with a bare "I'll adapt the script…" preamble
     * and zero tool calls. `{ type: 'any' }` lets the model pick which tool
     * (write_script in the common case, a source tool for schema discovery);
     * `{ type: 'tool', name }` pins a specific tool. Honored on both the
     * Anthropic path and the OpenAI/OpenRouter path (mapped to OpenAI's
     * tool_choice: 'required' / a named function).
     */
    firstIterationToolChoice?: { type: 'any' } | { type: 'tool'; name: string };
    /**
     * Internal — set only by the OpenRouter wrappers when the target is a Claude
     * model. Tells the OpenAI-wire path to emit Anthropic `cache_control`
     * breakpoints (OpenRouter forwards them to Anthropic for prompt caching).
     * Never set for direct OpenAI/Groq calls, so their requests are unchanged.
     */
    _openrouterClaudeCaching?: boolean;
    /**
     * Internal — OpenRouter provider-routing preferences (forwarded as the
     * `provider` body field). Set by the OpenRouter wrappers to steer routing to
     * a fast backend (e.g. {sort:'throughput'}). Never set for direct OpenAI/Groq.
     */
    _openrouterProvider?: Record<string, unknown>;
}

interface Tool {
    name: string;
    description: string;
    input_schema: {
        type: string;
        properties: Record<string, any>;
        required?: string[];
    };
}

interface ToolResult {
    tool_use_id: string;
    content: string;
    is_error?: boolean;
}

export class LLM {
    /* Get a complete text response from an LLM (Anthropic or Groq) */
    static async text(messages: LLMMessages, options: LLMOptions = {}): Promise<string> {
        const [provider, modelName] = this._parseModel(options.model);
        messages = this._sanitizeMessages(messages);

        if (provider === 'anthropic') {
            return this._anthropicText(messages, modelName, options);
        } else if (provider === 'groq') {
            return this._groqText(messages, modelName, options);
        } else if (provider === 'gemini') {
            return this._geminiText(messages, modelName, options);
        } else if (provider === 'openai') {
            return this._openaiText(messages, modelName, options);
        } else if (provider === 'openrouter') {
            return this._openrouterText(messages, modelName, options);
        } else {
            throw new Error(`Unsupported provider: ${provider}. Use "anthropic", "groq", "gemini", "openai", or "openrouter"`);
        }
    }

    /* Stream response from an LLM (Anthropic or Groq) */
    static async stream<T = string>(
        messages: LLMMessages,
        options: LLMOptions = {},
        json?: boolean
    ): Promise<T extends string ? string : any> {
        const [provider, modelName] = this._parseModel(options.model);
        messages = this._sanitizeMessages(messages);

        if (provider === 'anthropic') {
            return this._anthropicStream(messages, modelName, options, json);
        } else if (provider === 'groq') {
            return this._groqStream(messages, modelName, options, json);
        } else if (provider === 'gemini') {
            return this._geminiStream(messages, modelName, options, json);
        } else if (provider === 'openai') {
            return this._openaiStream(messages, modelName, options, json);
        } else if (provider === 'openrouter') {
            return this._openrouterStream(messages, modelName, options, json);
        } else {
            throw new Error(`Unsupported provider: ${provider}. Use "anthropic", "groq", "gemini", "openai", or "openrouter"`);
        }
    }

    /* Stream response with tool calling support (Anthropic and Gemini) */
    static async streamWithTools(
        messages: LLMMessages,
        tools: Tool[],
        toolHandler: (toolName: string, toolInput: any) => Promise<any>,
        options: LLMOptions = {},
        maxIterations: number = 3
    ): Promise<string> {
        const [provider, modelName] = this._parseModel(options.model);
        messages = this._sanitizeMessages(messages);

        if (provider === 'anthropic') {
            return this._anthropicStreamWithTools(messages, tools, toolHandler, modelName, options, maxIterations);
        } else if (provider === 'gemini') {
            return this._geminiStreamWithTools(messages, tools, toolHandler, modelName, options, maxIterations);
        } else if (provider === 'openai') {
            return this._openaiStreamWithTools(messages, tools, toolHandler, modelName, options, maxIterations);
        } else if (provider === 'openrouter') {
            return this._openrouterStreamWithTools(messages, tools, toolHandler, modelName, options, maxIterations);
        } else {
            throw new Error(`Tool calling is only supported for Anthropic, Gemini, OpenAI, and OpenRouter models`);
        }
    }

    // ============================================================
    // PRIVATE HELPER METHODS
    // ============================================================

    /**
     * Normalize system prompt to Anthropic format
     * Converts string to array format if needed
     * @param sys - System prompt (string or array of blocks)
     * @returns Normalized system prompt for Anthropic API
     */
    private static _normalizeSystemPrompt(sys: SystemPrompt): string | Anthropic.Messages.TextBlockParam[] {
        // If it's already a string, return as-is for backward compatibility
        if (typeof sys === 'string') {
            return sys;
        }

        // If it's an array, return it for caching support
        return sys;
    }

    /**
     * Strip unpaired UTF-16 surrogates from every text field of a message set.
     *
     * A lone surrogate (from mid-pair string slicing or corrupt source data)
     * serializes to a bare `\udXXX` escape that strict JSON parsers — including
     * the one on Anthropic's API — reject with "no low surrogate in string",
     * failing the whole request. Sanitizing here, at the single boundary every
     * provider call flows through, guarantees no request can carry one.
     */
    private static _sanitizeMessages(messages: LLMMessages): LLMMessages {
        const sys = typeof messages.sys === 'string'
            ? stripLoneSurrogates(messages.sys)
            : messages.sys.map(block =>
                block?.type === 'text' && typeof block.text === 'string'
                    ? { ...block, text: stripLoneSurrogates(block.text) }
                    : block
            );
        return {
            ...messages,
            sys,
            user: stripLoneSurrogates(messages.user),
            ...(messages.prefill !== undefined && { prefill: stripLoneSurrogates(messages.prefill) }),
        };
    }

    /**
     * Log cache usage metrics from Anthropic API response
     * Shows cache hits, costs, and savings
     */
    private static _logCacheUsage(usage: any): void {
        if (!usage) return;

        const inputTokens = usage.input_tokens || 0;
        const cacheCreationTokens = usage.cache_creation_input_tokens || 0;
        const cacheReadTokens = usage.cache_read_input_tokens || 0;
        const outputTokens = usage.output_tokens || 0;

        // Pricing (per 1M tokens) for Claude Haiku 4.5
        const INPUT_PRICE = 0.80;      // $0.80 per 1M tokens
        const OUTPUT_PRICE = 4.00;     // $4.00 per 1M tokens
        const CACHE_WRITE_PRICE = 1.00; // $1.00 per 1M tokens (25% markup)
        const CACHE_READ_PRICE = 0.08;  // $0.08 per 1M tokens (90% discount)

        // Calculate costs
        const regularInputCost = (inputTokens / 1_000_000) * INPUT_PRICE;
        const cacheWriteCost = (cacheCreationTokens / 1_000_000) * CACHE_WRITE_PRICE;
        const cacheReadCost = (cacheReadTokens / 1_000_000) * CACHE_READ_PRICE;
        const outputCost = (outputTokens / 1_000_000) * OUTPUT_PRICE;
        const totalCost = regularInputCost + cacheWriteCost + cacheReadCost + outputCost;

        // Calculate what it would have cost without caching
        const totalInputTokens = inputTokens + cacheCreationTokens + cacheReadTokens;
        const costWithoutCache = (totalInputTokens / 1_000_000) * INPUT_PRICE + outputCost;
        const savings = costWithoutCache - totalCost;
        const savingsPercent = costWithoutCache > 0 ? (savings / costWithoutCache * 100) : 0;

        // Log metrics
        console.log('\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        console.log('💰 PROMPT CACHING METRICS');
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

        console.log('\n📊 Token Usage:');
        console.log(`   Input (regular):     ${inputTokens.toLocaleString()} tokens`);
        if (cacheCreationTokens > 0) {
            console.log(`   Cache write:         ${cacheCreationTokens.toLocaleString()} tokens (first request)`);
        }
        if (cacheReadTokens > 0) {
            console.log(`   Cache read:          ${cacheReadTokens.toLocaleString()} tokens ⚡ HIT!`);
        }
        console.log(`   Output:              ${outputTokens.toLocaleString()} tokens`);
        console.log(`   Total input:         ${totalInputTokens.toLocaleString()} tokens`);

        console.log('\n💵 Cost Breakdown:');
        console.log(`   Input (regular):     $${regularInputCost.toFixed(6)}`);
        if (cacheCreationTokens > 0) {
            console.log(`   Cache write:         $${cacheWriteCost.toFixed(6)}`);
        }
        if (cacheReadTokens > 0) {
            console.log(`   Cache read:          $${cacheReadCost.toFixed(6)} (90% off!)`);
        }
        console.log(`   Output:              $${outputCost.toFixed(6)}`);
        console.log(`   ─────────────────────────────────────`);
        console.log(`   Total cost:          $${totalCost.toFixed(6)}`);

        if (cacheReadTokens > 0) {
            console.log(`\n💎 Savings:              $${savings.toFixed(6)} (${savingsPercent.toFixed(1)}% off)`);
            console.log(`   Without cache:       $${costWithoutCache.toFixed(6)}`);
        } else if (cacheCreationTokens > 0) {
            console.log(`\n⏱️  Cache created - next request will be ~90% cheaper!`);
        }

        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n');
    }

    /**
     * Parse model string to extract provider and model name
     * @param modelString - Format: "provider/model-name" or just "model-name"
     * @returns [provider, modelName]
     *
     * @example
     * "anthropic/claude-sonnet-4-5" → ["anthropic", "claude-sonnet-4-5"]
     * "groq/openai/gpt-oss-120b" → ["groq", "openai/gpt-oss-120b"]
     * "claude-sonnet-4-5" → ["anthropic", "claude-sonnet-4-5"] (default)
     */
    private static _parseModel(modelString?: string): [string, string] {
        let provider: string;
        let model: string;

        if (!modelString) {
            // Default to Anthropic Claude
            provider = 'anthropic';
            model = 'claude-sonnet-4-5';
        } else if (modelString.includes('/')) {
            // Split only on the FIRST slash to handle models like "groq/openai/gpt-oss-120b"
            const firstSlashIndex = modelString.indexOf('/');
            provider = modelString.substring(0, firstSlashIndex).toLowerCase().trim();
            model = modelString.substring(firstSlashIndex + 1).trim();
        } else {
            // No prefix, assume Anthropic
            provider = 'anthropic';
            model = modelString;
        }

        // OpenRouter fallback: when no Anthropic key is configured but an OpenRouter key is,
        // route every Claude call through OpenRouter (OpenAI-compatible) instead of api.anthropic.com.
        // This catches both configured models and the hardcoded `anthropic/...` defaults scattered
        // across the SDK, so nothing slips through to the (banned) Anthropic endpoint.
        if (provider === 'anthropic' && !process.env.ANTHROPIC_API_KEY && process.env.OPENROUTER_API_KEY) {
            return ['openrouter', 'anthropic/' + this._toOpenRouterSlug(model)];
        }

        // Explicit openrouter/anthropic/<id>: normalize a dated Anthropic id to OpenRouter's
        // slug (claude-haiku-4-5-20251001 -> claude-haiku-4.5) so both forms resolve.
        if (provider === 'openrouter' && model.startsWith('anthropic/')) {
            model = 'anthropic/' + this._toOpenRouterSlug(model.slice('anthropic/'.length));
        }

        return [provider, model];
    }

    /**
     * Map an Anthropic model id (e.g. "claude-sonnet-4-5-20250929") to the OpenRouter slug
     * (e.g. "claude-sonnet-4.5"). OpenRouter slugs drop the date suffix and use dotted versions.
     */
    private static _toOpenRouterSlug(model: string): string {
        const map: Record<string, string> = {
            'claude-sonnet-4-5-20250929': 'claude-sonnet-4.5',
            'claude-sonnet-4-5': 'claude-sonnet-4.5',
            'claude-haiku-4-5-20251001': 'claude-haiku-4.5',
            'claude-haiku-4-5': 'claude-haiku-4.5',
            'claude-opus-4-1-20250805': 'claude-opus-4.1',
            'claude-opus-4-1': 'claude-opus-4.1',
        };
        if (map[model]) return map[model];
        // Generic fallback: strip a trailing -YYYYMMDD date, then turn a -N-N version into -N.N
        let m = model.replace(/-\d{8}$/, '');
        m = m.replace(/-(\d+)-(\d+)$/, '-$1.$2');
        return m;
    }

    /**
     * Per-provider proxy base URL. Returns `${SUPERATOM_LLM_PROXY_URL}/<provider>`
     * when our Cloudflare LLM proxy is configured, else undefined (→ talk to the
     * provider directly, legacy behaviour). An explicit options.baseURL (e.g.
     * OpenRouter) always wins and is never overridden. See backend/docs/llm-proxy.md.
     */
    private static _proxyBaseURL(provider: string, options: LLMOptions): string | undefined {
        if (options.baseURL) return options.baseURL;
        const root = process.env.SUPERATOM_LLM_PROXY_URL;
        return root ? `${root.replace(/\/+$/, '')}/${provider}` : undefined;
    }

    // ============================================================
    // OPENROUTER IMPLEMENTATION
    // OpenRouter is OpenAI-API-compatible, so these delegate to the OpenAI methods
    // but force OpenRouter's own key + base URL. The real OpenAI path is untouched.
    // ============================================================

    private static _openrouterOptions(options: LLMOptions, modelName: string): LLMOptions {
        // Route OpenRouter through our proxy (/openrouter) when configured, so it's
        // metered per-client like every other provider. Precedence: an explicit
        // options.baseURL or OPENROUTER_BASE_URL still wins (local overrides), then
        // the proxy, then OpenRouter direct. See backend/docs/llm-proxy.md.
        const root = process.env.SUPERATOM_LLM_PROXY_URL;
        const baseURL =
            options.baseURL ||
            process.env.OPENROUTER_BASE_URL ||
            (root ? `${root.replace(/\/+$/, '')}/openrouter` : 'https://openrouter.ai/api/v1');
        const isClaude = this._isClaudeModel(modelName);
        return {
            ...options,
            apiKey: process.env.OPENROUTER_API_KEY || options.apiKey,
            baseURL,
            // Enable Anthropic prompt caching on the OpenAI-wire path for Claude
            // models routed via OpenRouter (no-op for non-Claude models).
            _openrouterClaudeCaching: isClaude,
            // Prefer Anthropic's first-party backend for Claude (best codegen +
            // prompt-caching parity), but list the equivalent Bedrock/Vertex routes
            // next so OpenRouter can serve the SAME model weights from them when
            // Anthropic first-party rejects a request — notably the intermittent
            // "403 This model is not available in your region." that Anthropic
            // returns for geo-restricted accounts (allow_fallbacks does NOT cover
            // that 4xx on its own). Override the order via OPENROUTER_PROVIDER_ORDER
            // (comma-separated) if needed.
            // Pin these Anthropic-served backends ONLY for Claude; non-Claude test models
            // (openrouter/openai|google/...) must route freely or these can't serve them.
            ...(isClaude ? {
                _openrouterProvider: {
                    order: (process.env.OPENROUTER_PROVIDER_ORDER || 'Anthropic,Amazon Bedrock,Google Vertex')
                        .split(',').map(s => s.trim()),
                    allow_fallbacks: true,
                },
            } : {}),
        };
    }

    // Region/availability/transient errors worth a fresh retry (never auth/bad-request).
    private static _isRetryableProviderError(err: any): boolean {
        const status = Number(err?.status ?? err?.response?.status ?? 0);
        const msg = String(err?.message || err?.error?.message || err?.response?.data?.error?.message || err || '').toLowerCase();
        if (/api key|unauthor|invalid.*key|authentication|permission denied/.test(msg)) return false;
        if (status === 400 || status === 401 || status === 404 || status === 422) return false;
        if (status === 429 || (status >= 500 && status <= 599)) return true;
        return /region|not available|not present|no (allowed )?provider|no endpoints|unavailable|overloaded|temporarily|capacity|timeout|timed out|try again/.test(msg);
    }

    // Retry OpenRouter on the intermittent "model not available in this region" + 429/5xx that
    // OpenRouter won't fail over itself. Attempt 1 keeps Anthropic first (caching); retries
    // deprioritize it so a healthier backend serves. Never retries auth/4xx or user-abort.
    private static async _withOpenrouterRetry<T>(
        label: string,
        modelName: string,
        options: LLMOptions,
        call: (opts: LLMOptions) => Promise<T>,
    ): Promise<T> {
        const isClaude = this._isClaudeModel(modelName);
        const maxAttempts = 3;
        let lastErr: any;
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            let opts = this._openrouterOptions(options, modelName);
            if (attempt > 1 && isClaude && (opts as any)._openrouterProvider) {
                opts = { ...opts, _openrouterProvider: { order: ['Amazon Bedrock', 'Google Vertex', 'Anthropic'], allow_fallbacks: true } };
            }
            try {
                return await call(opts);
            } catch (err: any) {
                lastErr = err;
                if (options.signal?.aborted || err?.name === 'AbortError') throw err;
                const status = err?.status ?? err?.response?.status ?? '?';
                const body = err?.error?.message || err?.message || String(err);
                console.warn(`[LLM/OpenRouter] ${label} attempt ${attempt}/${maxAttempts} failed | model=${modelName} status=${status} | ${String(body).slice(0, 300)}`);
                if (attempt < maxAttempts && this._isRetryableProviderError(err)) continue;
                throw err;
            }
        }
        throw lastErr;
    }

    private static async _openrouterText(messages: LLMMessages, modelName: string, options: LLMOptions): Promise<string> {
        return this._withOpenrouterRetry('text', modelName, options, (opts) => this._openaiText(messages, modelName, opts));
    }

    private static async _openrouterStream(messages: LLMMessages, modelName: string, options: LLMOptions, json?: boolean): Promise<any> {
        return this._withOpenrouterRetry('stream', modelName, options, (opts) => this._openaiStream(messages, modelName, opts, json));
    }

    private static async _openrouterStreamWithTools(
        messages: LLMMessages,
        tools: Tool[],
        toolHandler: (toolName: string, toolInput: any) => Promise<any>,
        modelName: string,
        options: LLMOptions,
        maxIterations: number
    ): Promise<string> {
        return this._withOpenrouterRetry('streamWithTools', modelName, options, (opts) => this._openaiStreamWithTools(messages, tools, toolHandler, modelName, opts, maxIterations));
    }

    // ============================================================
    // ANTHROPIC IMPLEMENTATION
    // ============================================================

    /**
     * Build an Anthropic client. Routes through our Cloudflare LLM proxy when
     * SUPERATOM_LLM_PROXY_URL is set (each client ships a per-client proxy key as
     * ANTHROPIC_API_KEY and never holds the real key); otherwise talks to
     * api.anthropic.com directly. See backend/docs/llm-proxy.md.
     */
    private static _anthropicClient(options: LLMOptions): Anthropic {
        const apiKey = options.apiKey || process.env.ANTHROPIC_API_KEY || "";
        const baseURL = this._proxyBaseURL('anthropic', options);
        return new Anthropic({ apiKey, ...(baseURL ? { baseURL } : {}) });
    }

    /** True when OpenRouter is configured as a fail-open fallback for Claude. */
    private static _openrouterAvailable(): boolean {
        return !!process.env.OPENROUTER_API_KEY;
    }

    /** Remap an Anthropic model id to the OpenRouter model path for fail-open. */
    private static _anthropicFallbackModel(modelName: string): string {
        return 'anthropic/' + this._toOpenRouterSlug(modelName);
    }

    private static async _anthropicText(
        messages: LLMMessages,
        modelName: string,
        options: LLMOptions
    ): Promise<string> {
        const startTime = Date.now();
        const requestId = llmUsageLogger.generateRequestId();
        const client = this._anthropicClient(options);

        try {
            if (options.signal?.aborted) throw new RequestAbortedError();
            const response = await client.messages.create({
                model: modelName,
                max_tokens: options.maxTokens || 1000,
                temperature: options.temperature,
                system: this._normalizeSystemPrompt(messages.sys) as any,
                messages: [{
                    role: "user",
                    content: messages.user
                }]
            }, { signal: options.signal });

            const durationMs = Date.now() - startTime;
            const usage = response.usage as any;

            // Log usage
            const inputTokens = usage?.input_tokens || 0;
            const outputTokens = usage?.output_tokens || 0;
            const cacheReadTokens = usage?.cache_read_input_tokens || 0;
            const cacheWriteTokens = usage?.cache_creation_input_tokens || 0;

            llmUsageLogger.log({
                timestamp: new Date().toISOString(),
                requestId,
                provider: 'anthropic',
                model: modelName,
                method: 'text',
                inputTokens,
                outputTokens,
                cacheReadTokens,
                cacheWriteTokens,
                totalTokens: inputTokens + outputTokens + cacheReadTokens + cacheWriteTokens,
                costUSD: llmUsageLogger.calculateCost(modelName, inputTokens, outputTokens, cacheReadTokens, cacheWriteTokens),
                durationMs,
                success: true,
            });

            const textBlock = response.content.find(block => block.type === 'text');
            return textBlock?.type === 'text' ? textBlock.text : '';
        } catch (error) {
            const durationMs = Date.now() - startTime;
            llmUsageLogger.log({
                timestamp: new Date().toISOString(),
                requestId,
                provider: 'anthropic',
                model: modelName,
                method: 'text',
                inputTokens: 0,
                outputTokens: 0,
                totalTokens: 0,
                costUSD: 0,
                durationMs,
                success: false,
                error: error instanceof Error ? error.message : String(error),
            });
            // User cancelled — clean abort, never fail-open to another provider.
            if (options.signal?.aborted || isAbortError(error)) {
                throw new RequestAbortedError();
            }
            // Fail-open: retry through OpenRouter so a proxy/Anthropic outage
            // doesn't take the client down. Safe — nothing emitted to the caller yet.
            if (this._openrouterAvailable()) {
                return this._openrouterText(messages, this._anthropicFallbackModel(modelName), options);
            }
            throw error;
        }
    }

    private static async _anthropicStream(
        messages: LLMMessages,
        modelName: string,
        options: LLMOptions,
        json?: boolean
    ): Promise<any> {
        const startTime = Date.now();
        const requestId = llmUsageLogger.generateRequestId();
        const client = this._anthropicClient(options);

        let streamedAnyText = false;
        try {
            // Build messages array
            const apiMessages: Anthropic.Messages.MessageParam[] = [{
                role: "user",
                content: messages.user
            }];

            // Add prefill to force specific output format (e.g., "{" for JSON)
            // For JSON mode, automatically prefill with "{" if no explicit prefill provided
            const prefill = messages.prefill || (json ? "{" : undefined);
            if (prefill) {
                apiMessages.push({
                    role: "assistant",
                    content: prefill
                });
            }

            if (options.signal?.aborted) throw new RequestAbortedError();
            const stream = await client.messages.create({
                model: modelName,
                max_tokens: options.maxTokens || 1000,
                temperature: options.temperature,
                system: this._normalizeSystemPrompt(messages.sys) as any,
                messages: apiMessages,
                stream: true,
            }, { signal: options.signal });

            // Start with prefill if provided (since model continues from there)
            let fullText = prefill || '';
            let usage: any = null;
            let inputTokens = 0;
            let outputTokens = 0;
            let cacheReadTokens = 0;
            let cacheWriteTokens = 0;

            // Process stream
            for await (const chunk of stream) {
                if (chunk.type === 'content_block_delta' && chunk.delta.type === 'text_delta') {
                    const text = chunk.delta.text;
                    fullText += text;

                    // Call partial callback if provided
                    if (options.partial) {
                        options.partial(text);
                        streamedAnyText = true;
                    }
                } else if (chunk.type === 'message_start' && (chunk as any).message?.usage) {
                    // Capture input tokens from message_start
                    const msgUsage = (chunk as any).message.usage;
                    inputTokens = msgUsage.input_tokens || 0;
                    cacheReadTokens = msgUsage.cache_read_input_tokens || 0;
                    cacheWriteTokens = msgUsage.cache_creation_input_tokens || 0;
                } else if (chunk.type === 'message_delta' && (chunk as any).usage) {
                    // Capture output tokens from message_delta event
                    usage = (chunk as any).usage;
                    outputTokens = usage.output_tokens || 0;
                }
            }

            const durationMs = Date.now() - startTime;

            // Log usage
            llmUsageLogger.log({
                timestamp: new Date().toISOString(),
                requestId,
                provider: 'anthropic',
                model: modelName,
                method: 'stream',
                inputTokens,
                outputTokens,
                cacheReadTokens,
                cacheWriteTokens,
                totalTokens: inputTokens + outputTokens + cacheReadTokens + cacheWriteTokens,
                costUSD: llmUsageLogger.calculateCost(modelName, inputTokens, outputTokens, cacheReadTokens, cacheWriteTokens),
                durationMs,
                success: true,
            });

            // Return parsed JSON or text
            if (json) {
                return this._parseJSON(fullText);
            }

            return fullText;
        } catch (error) {
            const durationMs = Date.now() - startTime;
            llmUsageLogger.log({
                timestamp: new Date().toISOString(),
                requestId,
                provider: 'anthropic',
                model: modelName,
                method: 'stream',
                inputTokens: 0,
                outputTokens: 0,
                totalTokens: 0,
                costUSD: 0,
                durationMs,
                success: false,
                error: error instanceof Error ? error.message : String(error),
            });
            // User cancelled — surface a clean abort, never fail-open to another
            // provider (that would re-run the cancelled work).
            if (options.signal?.aborted || isAbortError(error)) {
                throw new RequestAbortedError();
            }
            // Fail-open: if nothing was streamed to the caller yet, retry through
            // OpenRouter so a proxy/Anthropic outage doesn't take the client down.
            if (!streamedAnyText && this._openrouterAvailable()) {
                return this._openrouterStream(messages, this._anthropicFallbackModel(modelName), options, json);
            }
            throw error;
        }
    }

    private static async _anthropicStreamWithTools(
        messages: LLMMessages,
        tools: Tool[],
        toolHandler: (toolName: string, toolInput: any) => Promise<any>,
        modelName: string,
        options: LLMOptions,
        maxIterations: number
    ): Promise<string> {
        const methodStartTime = Date.now();
        const client = this._anthropicClient(options);

        // Conversation history for tool calling loop
        const conversationMessages: Anthropic.MessageParam[] = [{
            role: "user",
            content: messages.user
        }];

        let iterations = 0;
        let finalText = '';
        let totalToolCalls = 0;

        // Track cumulative usage across all iterations
        let totalInputTokens = 0;
        let totalOutputTokens = 0;
        let totalCacheReadTokens = 0;
        let totalCacheWriteTokens = 0;

        while (iterations < maxIterations) {
            // Abort checkpoint: stop before starting a fresh LLM turn (and before
            // re-entering after tools ran). Cancellation that lands between turns
            // is caught here so we never kick off another round-trip.
            if (options.signal?.aborted) throw new RequestAbortedError();

            iterations++;
            const iterationStartTime = Date.now();
            const requestId = llmUsageLogger.generateRequestId();

            // Force a tool call on the first iteration only (fork mode). After
            // iter 1 we revert to auto so execute_script + the final analysis
            // text can flow normally. Without this, the model can end_turn with
            // a bare preamble and zero tool calls — see firstIterationToolChoice.
            const toolChoice = (iterations === 1 && options.firstIterationToolChoice)
                ? options.firstIterationToolChoice
                : undefined;

            // Use streaming API
            let stream: any;
            try {
                stream = await client.messages.create({
                    model: modelName,
                    max_tokens: options.maxTokens || 4000,
                    temperature: options.temperature,
                    system: this._normalizeSystemPrompt(messages.sys) as any,
                    messages: conversationMessages,
                    tools: tools as any,
                    ...(toolChoice ? { tool_choice: toolChoice as any } : {}),
                    stream: true, // Enable streaming
                }, { signal: options.signal });
            } catch (error) {
                // User cancelled — surface a clean abort, never fail-open (that
                // would re-run the cancelled tool loop through another provider).
                if (options.signal?.aborted || isAbortError(error)) {
                    throw new RequestAbortedError();
                }
                // Fail-open only on the very first request, before any text was
                // streamed or any tool ran — replaying a partially-executed tool
                // loop through another provider could re-run write_script / queries.
                if (iterations === 1 && totalToolCalls === 0 && finalText === '' && this._openrouterAvailable()) {
                    return this._openrouterStreamWithTools(
                        messages, tools, toolHandler,
                        this._anthropicFallbackModel(modelName), options, maxIterations
                    );
                }
                throw error;
            }

            // Collect the response content and stop reason
            let stopReason: string | null = null;
            const contentBlocks: any[] = [];
            let currentTextBlock = '';
            let currentToolUse: any = null;
            let inputTokens = 0;
            let outputTokens = 0;
            let cacheReadTokens = 0;
            let cacheWriteTokens = 0;

            // Process the stream
            for await (const chunk of stream) {
                if (chunk.type === 'message_start') {
                    // Reset for new message
                    contentBlocks.length = 0;
                    currentTextBlock = '';
                    currentToolUse = null;
                    // Capture input tokens from message_start
                    const msgUsage = (chunk as any).message?.usage;
                    if (msgUsage) {
                        inputTokens = msgUsage.input_tokens || 0;
                        cacheReadTokens = msgUsage.cache_read_input_tokens || 0;
                        cacheWriteTokens = msgUsage.cache_creation_input_tokens || 0;
                    }
                }

                if (chunk.type === 'content_block_start') {
                    if (chunk.content_block.type === 'text') {
                        currentTextBlock = '';
                    } else if (chunk.content_block.type === 'tool_use') {
                        currentToolUse = {
                            type: 'tool_use',
                            id: chunk.content_block.id,
                            name: chunk.content_block.name,
                            input: {}
                        };
                    }
                }

                if (chunk.type === 'content_block_delta') {
                    if (chunk.delta.type === 'text_delta') {
                        // Stream text chunks to callback
                        const text = chunk.delta.text;
                        currentTextBlock += text;
                        if (options.partial) {
                            options.partial(text);
                        }
                    } else if (chunk.delta.type === 'input_json_delta' && currentToolUse) {
                        // Accumulate tool input JSON
                        currentToolUse.inputJson = (currentToolUse.inputJson || '') + chunk.delta.partial_json;
                    }
                }

                if (chunk.type === 'content_block_stop') {
                    if (currentTextBlock) {
                        contentBlocks.push({
                            type: 'text',
                            text: currentTextBlock
                        });
                        finalText = currentTextBlock; // Keep track of final text
                        currentTextBlock = '';
                    } else if (currentToolUse) {
                        // Parse the accumulated JSON input
                        try {
                            currentToolUse.input = currentToolUse.inputJson ? JSON.parse(currentToolUse.inputJson) : {};
                        } catch (error) {
                            currentToolUse.input = {};
                        }
                        delete currentToolUse.inputJson;
                        contentBlocks.push(currentToolUse);
                        currentToolUse = null;
                    }
                }

                if (chunk.type === 'message_delta') {
                    stopReason = chunk.delta.stop_reason || stopReason;
                    // Capture output tokens from message_delta
                    if ((chunk as any).usage) {
                        outputTokens = (chunk as any).usage.output_tokens || 0;
                    }
                }

                if (chunk.type === 'message_stop') {
                    // Message complete
                    break;
                }
            }

            const iterationDuration = Date.now() - iterationStartTime;
            const toolUsesInIteration = contentBlocks.filter(block => block.type === 'tool_use').length;
            totalToolCalls += toolUsesInIteration;

            // Update cumulative totals
            totalInputTokens += inputTokens;
            totalOutputTokens += outputTokens;
            totalCacheReadTokens += cacheReadTokens;
            totalCacheWriteTokens += cacheWriteTokens;

            // Log this iteration
            llmUsageLogger.log({
                timestamp: new Date().toISOString(),
                requestId,
                provider: 'anthropic',
                model: modelName,
                method: `streamWithTools[iter=${iterations}]`,
                inputTokens,
                outputTokens,
                cacheReadTokens,
                cacheWriteTokens,
                totalTokens: inputTokens + outputTokens + cacheReadTokens + cacheWriteTokens,
                costUSD: llmUsageLogger.calculateCost(modelName, inputTokens, outputTokens, cacheReadTokens, cacheWriteTokens),
                durationMs: iterationDuration,
                toolCalls: toolUsesInIteration,
                success: true,
            });

            // Diagnostic: surface the per-iteration stop reason + tool count so we
            // can see WHY the loop ended (e.g. max_tokens truncating a tool call).
            // Debug-gated — set SA_DEBUG_LLM_TOOLS=1 to enable; fires on every
            // streamWithTools call (source agents too), so off by default.
            if (process.env.SA_DEBUG_LLM_TOOLS === '1') {
                console.log(
                    `[LLM] streamWithTools iter=${iterations} stop_reason=${stopReason} ` +
                    `toolUses=${toolUsesInIteration} textLen=${finalText.length} ` +
                    `forcedToolChoice=${toolChoice ? JSON.stringify(toolChoice) : 'none'} outTokens=${outputTokens}`
                );
            }

            // Find any COMPLETE tool-use blocks the model produced this iteration.
            // We key off the presence of tool calls rather than stop_reason alone:
            // a forced tool_choice can finish with stop_reason='max_tokens' yet
            // still carry a fully-parsed tool_use — dropping it (the old
            // `else { break }`) left the turn with 0 tools and empty text.
            const toolUses = contentBlocks.filter(block => block.type === 'tool_use');

            // Check stop reason
            if (stopReason === 'end_turn' && toolUses.length === 0) {
                // Final text response already streamed
                break;
            }

            if (toolUses.length > 0) {
                if (stopReason === 'max_tokens') {
                    console.warn(
                        `[LLM] streamWithTools: stop_reason=max_tokens with a tool call — ` +
                        `the tool input may be truncated. Consider raising maxTokens (current ${options.maxTokens || 4000}).`
                    );
                }

                // Add assistant's response to conversation
                conversationMessages.push({
                    role: "assistant",
                    content: contentBlocks
                });

                // Execute all tools and collect results
                const toolResults: Anthropic.MessageParam = {
                    role: "user",
                    content: []
                };

                // Execute all tools in parallel for lower latency
                const toolResultEntries = await Promise.all(toolUses.map(async (toolUse) => {
                    try {
                        const result = await toolHandler(toolUse.name, toolUse.input);
                        // Truncate very large results to reduce memory and token usage
                        let resultContent = typeof result === 'string' ? result : JSON.stringify(result);
                        const MAX_RESULT_LENGTH = 50000; // ~12k tokens max per tool result
                        if (resultContent.length > MAX_RESULT_LENGTH) {
                            // Surrogate-aware cut so the truncation can't split a UTF-16 pair.
                            resultContent = safeTruncate(resultContent, MAX_RESULT_LENGTH) +
                                '\n\n... [Result truncated - showing first 50000 characters of ' + resultContent.length + ' total]';
                        }
                        return {
                            type: 'tool_result' as const,
                            tool_use_id: toolUse.id,
                            // Final safety net: tool results carry source data and are built
                            // mid-loop (after entry-point sanitize), so strip lone surrogates here.
                            content: stripLoneSurrogates(resultContent)
                        };
                    } catch (error) {
                        return {
                            type: 'tool_result' as const,
                            tool_use_id: toolUse.id,
                            content: error instanceof Error ? error.message : String(error),
                            is_error: true
                        };
                    }
                }));
                toolResultEntries.forEach(entry => (toolResults.content as any[]).push(entry));

                // Add tool results to conversation
                conversationMessages.push(toolResults);
            } else {
                // Unexpected stop reason, final text already streamed
                break;
            }
        }

        // Log total summary for all iterations
        const totalDuration = Date.now() - methodStartTime;
        if (iterations > 1) {
            llmUsageLogger.log({
                timestamp: new Date().toISOString(),
                requestId: llmUsageLogger.generateRequestId(),
                provider: 'anthropic',
                model: modelName,
                method: `streamWithTools[TOTAL:${iterations}iters]`,
                inputTokens: totalInputTokens,
                outputTokens: totalOutputTokens,
                cacheReadTokens: totalCacheReadTokens,
                cacheWriteTokens: totalCacheWriteTokens,
                totalTokens: totalInputTokens + totalOutputTokens + totalCacheReadTokens + totalCacheWriteTokens,
                costUSD: llmUsageLogger.calculateCost(modelName, totalInputTokens, totalOutputTokens, totalCacheReadTokens, totalCacheWriteTokens),
                durationMs: totalDuration,
                toolCalls: totalToolCalls,
                success: true,
            });
        }

        if (iterations >= maxIterations) {
            throw new Error(`Max iterations (${maxIterations}) reached in tool calling loop`);
        }

        return finalText;
    }

    // ============================================================
    // GROQ IMPLEMENTATION
    // ============================================================

    private static async _groqText(
        messages: LLMMessages,
        modelName: string,
        options: LLMOptions
    ): Promise<string> {
        const startTime = Date.now();
        const requestId = llmUsageLogger.generateRequestId();
        const client = new Groq({
            apiKey: options.apiKey || process.env.GROQ_API_KEY || "",
            baseURL: this._proxyBaseURL('groq', options),
        });

        try {
            if (options.signal?.aborted) throw new RequestAbortedError();
            const response = await client.chat.completions.create({
                model: modelName,
                messages: [
                    { role: 'system', content: messages.sys as string },
                    { role: 'user', content: messages.user }
                ],
                temperature: options.temperature,
                max_tokens: options.maxTokens || 1000,
            }, { signal: options.signal });

            const durationMs = Date.now() - startTime;
            const usage = response.usage;
            const inputTokens = usage?.prompt_tokens || 0;
            const outputTokens = usage?.completion_tokens || 0;

            llmUsageLogger.log({
                timestamp: new Date().toISOString(),
                requestId,
                provider: 'groq',
                model: modelName,
                method: 'text',
                inputTokens,
                outputTokens,
                totalTokens: inputTokens + outputTokens,
                costUSD: llmUsageLogger.calculateCost(modelName, inputTokens, outputTokens),
                durationMs,
                success: true,
            });

            return response.choices[0]?.message?.content || '';
        } catch (error) {
            const durationMs = Date.now() - startTime;
            llmUsageLogger.log({
                timestamp: new Date().toISOString(),
                requestId,
                provider: 'groq',
                model: modelName,
                method: 'text',
                inputTokens: 0,
                outputTokens: 0,
                totalTokens: 0,
                costUSD: 0,
                durationMs,
                success: false,
                error: error instanceof Error ? error.message : String(error),
            });
            throw error;
        }
    }

    private static async _groqStream(
        messages: LLMMessages,
        modelName: string,
        options: LLMOptions,
        json?: boolean
    ): Promise<any> {
        const startTime = Date.now();
        const requestId = llmUsageLogger.generateRequestId();
        const apiKey = options.apiKey || process.env.GROQ_API_KEY || "";
        const client = new Groq({
            apiKey: apiKey,
        });

        try {
            if (options.signal?.aborted) throw new RequestAbortedError();
            const stream = await client.chat.completions.create({
                model: modelName,
                messages: [
                    { role: 'system', content: messages.sys as string },
                    { role: 'user', content: messages.user }
                ],
                temperature: options.temperature,
                max_tokens: options.maxTokens || 1000,
                stream: true,
                response_format: json ? { type: 'json_object' } : undefined
            }, { signal: options.signal });

            let fullText = '';
            let inputTokens = 0;
            let outputTokens = 0;

            // Process stream
            for await (const chunk of stream) {
                const text = chunk.choices[0]?.delta?.content || '';
                if (text) {
                    fullText += text;

                    // Call partial callback if provided
                    if (options.partial) {
                        options.partial(text);
                    }
                }
                // Groq provides usage in the last chunk
                if ((chunk as any).x_groq?.usage) {
                    inputTokens = (chunk as any).x_groq.usage.prompt_tokens || 0;
                    outputTokens = (chunk as any).x_groq.usage.completion_tokens || 0;
                }
            }

            const durationMs = Date.now() - startTime;

            // Estimate tokens if not provided (Groq streaming may not always provide usage)
            if (inputTokens === 0) {
                // Rough estimate: 1 token ≈ 4 characters
                const sysPrompt = typeof messages.sys === 'string' ? messages.sys : messages.sys.map(b => b.text).join('');
                inputTokens = Math.ceil((sysPrompt.length + messages.user.length) / 4);
            }
            if (outputTokens === 0) {
                outputTokens = Math.ceil(fullText.length / 4);
            }

            llmUsageLogger.log({
                timestamp: new Date().toISOString(),
                requestId,
                provider: 'groq',
                model: modelName,
                method: 'stream',
                inputTokens,
                outputTokens,
                totalTokens: inputTokens + outputTokens,
                costUSD: llmUsageLogger.calculateCost(modelName, inputTokens, outputTokens),
                durationMs,
                success: true,
            });

            // Return parsed JSON or text
            if (json) {
                return this._parseJSON(fullText);
            }

            return fullText;
        } catch (error) {
            const durationMs = Date.now() - startTime;
            llmUsageLogger.log({
                timestamp: new Date().toISOString(),
                requestId,
                provider: 'groq',
                model: modelName,
                method: 'stream',
                inputTokens: 0,
                outputTokens: 0,
                totalTokens: 0,
                costUSD: 0,
                durationMs,
                success: false,
                error: error instanceof Error ? error.message : String(error),
            });
            throw error;
        }
    }

    // ============================================================
    // GEMINI IMPLEMENTATION
    // ============================================================

    /**
     * Gemini request options carrying the proxy base URL, or undefined → talk to
     * generativelanguage.googleapis.com directly. The Google SDK takes baseUrl as a
     * per-model request option, not a constructor arg. See backend/docs/llm-proxy.md.
     */
    private static _geminiRequestOptions(options: LLMOptions): { baseUrl?: string; signal?: AbortSignal } | undefined {
        const baseUrl = this._proxyBaseURL('gemini', options);
        // RequestOptions.signal is honored by @google/generative-ai (>=0.14): it
        // aborts generateContent/generateContentStream/sendMessageStream made by
        // the model instance built with these options.
        if (!baseUrl && !options.signal) return undefined;
        return {
            ...(baseUrl ? { baseUrl } : {}),
            ...(options.signal ? { signal: options.signal } : {}),
        };
    }

    private static async _geminiText(
        messages: LLMMessages,
        modelName: string,
        options: LLMOptions
    ): Promise<string> {
        const startTime = Date.now();
        const requestId = llmUsageLogger.generateRequestId();
        const apiKey = options.apiKey || process.env.GEMINI_API_KEY || "";
        const genAI = new GoogleGenerativeAI(apiKey);

        // Convert system prompt to string if it's an array
        const systemPrompt = typeof messages.sys === 'string'
            ? messages.sys
            : messages.sys.map(block => block.text).join('\n');

        try {
            if (options.signal?.aborted) throw new RequestAbortedError();
            const model = genAI.getGenerativeModel({
                model: modelName,
                systemInstruction: systemPrompt,
                generationConfig: {
                    maxOutputTokens: options.maxTokens || 1000,
                    temperature: options.temperature,
                    topP: options.topP,
                }
            }, this._geminiRequestOptions(options));

            const result = await model.generateContent(messages.user);
            const response = await result.response;
            const text = response.text();
            const durationMs = Date.now() - startTime;

            // Gemini provides usage metadata
            const usage = response.usageMetadata;
            const inputTokens = usage?.promptTokenCount || Math.ceil((systemPrompt.length + messages.user.length) / 4);
            const outputTokens = usage?.candidatesTokenCount || Math.ceil(text.length / 4);

            llmUsageLogger.log({
                timestamp: new Date().toISOString(),
                requestId,
                provider: 'gemini',
                model: modelName,
                method: 'text',
                inputTokens,
                outputTokens,
                totalTokens: inputTokens + outputTokens,
                costUSD: llmUsageLogger.calculateCost(modelName, inputTokens, outputTokens),
                durationMs,
                success: true,
            });

            return text;
        } catch (error) {
            const durationMs = Date.now() - startTime;
            llmUsageLogger.log({
                timestamp: new Date().toISOString(),
                requestId,
                provider: 'gemini',
                model: modelName,
                method: 'text',
                inputTokens: 0,
                outputTokens: 0,
                totalTokens: 0,
                costUSD: 0,
                durationMs,
                success: false,
                error: error instanceof Error ? error.message : String(error),
            });
            throw error;
        }
    }

    private static async _geminiStream(
        messages: LLMMessages,
        modelName: string,
        options: LLMOptions,
        json?: boolean
    ): Promise<any> {
        const startTime = Date.now();
        const requestId = llmUsageLogger.generateRequestId();
        const apiKey = options.apiKey || process.env.GEMINI_API_KEY || "";
        const genAI = new GoogleGenerativeAI(apiKey);

        // Convert system prompt to string if it's an array
        const systemPrompt = typeof messages.sys === 'string'
            ? messages.sys
            : messages.sys.map(block => block.text).join('\n');

        try {
            if (options.signal?.aborted) throw new RequestAbortedError();
            // For JSON mode with partial callback, use streaming for early answer component extraction
            // but wait for complete response before parsing JSON
            if (json && options.partial) {
                const model = genAI.getGenerativeModel({
                    model: modelName,
                    systemInstruction: systemPrompt,
                    generationConfig: {
                        maxOutputTokens: options.maxTokens || 1000,
                        temperature: options.temperature,
                        topP: options.topP,
                        responseMimeType: 'application/json',
                    }
                }, this._geminiRequestOptions(options));

                const result = await model.generateContentStream(messages.user);
                let fullText = '';
                let inputTokens = 0;
                let outputTokens = 0;

                // Stream chunks to partial callback for early answer component extraction
                for await (const chunk of result.stream) {
                    try {
                        const text = chunk.text();
                        if (text) {
                            fullText += text;
                            options.partial(text);  // Enable early answer component detection
                        }
                    } catch (chunkError) {
                        // Some chunks may not have text, continue
                    }
                    if (chunk.usageMetadata) {
                        inputTokens = chunk.usageMetadata.promptTokenCount || 0;
                        outputTokens = chunk.usageMetadata.candidatesTokenCount || 0;
                    }
                }

                const durationMs = Date.now() - startTime;
                if (inputTokens === 0) {
                    inputTokens = Math.ceil((systemPrompt.length + messages.user.length) / 4);
                }
                if (outputTokens === 0) {
                    outputTokens = Math.ceil(fullText.length / 4);
                }

                llmUsageLogger.log({
                    timestamp: new Date().toISOString(),
                    requestId,
                    provider: 'gemini',
                    model: modelName,
                    method: 'stream-json-partial',
                    inputTokens,
                    outputTokens,
                    totalTokens: inputTokens + outputTokens,
                    costUSD: llmUsageLogger.calculateCost(modelName, inputTokens, outputTokens),
                    durationMs,
                    success: true,
                });

                // Parse complete JSON after stream finishes
                return this._parseJSON(fullText);
            }

            // For JSON mode without partial callback, use non-streaming for reliability
            if (json) {
                const model = genAI.getGenerativeModel({
                    model: modelName,
                    systemInstruction: systemPrompt,
                    generationConfig: {
                        maxOutputTokens: options.maxTokens || 1000,
                        temperature: options.temperature,
                        topP: options.topP,
                        responseMimeType: 'application/json',
                    }
                }, this._geminiRequestOptions(options));

                const result = await model.generateContent(messages.user);
                const response = result.response;
                const fullText = response.text();
                const durationMs = Date.now() - startTime;

                // Capture usage
                const usage = response.usageMetadata;
                const inputTokens = usage?.promptTokenCount || Math.ceil((systemPrompt.length + messages.user.length) / 4);
                const outputTokens = usage?.candidatesTokenCount || Math.ceil(fullText.length / 4);

                llmUsageLogger.log({
                    timestamp: new Date().toISOString(),
                    requestId,
                    provider: 'gemini',
                    model: modelName,
                    method: 'stream-json',
                    inputTokens,
                    outputTokens,
                    totalTokens: inputTokens + outputTokens,
                    costUSD: llmUsageLogger.calculateCost(modelName, inputTokens, outputTokens),
                    durationMs,
                    success: true,
                });

                return this._parseJSON(fullText);
            }

            // For text mode, use streaming as before
            const model = genAI.getGenerativeModel({
                model: modelName,
                systemInstruction: systemPrompt,
                generationConfig: {
                    maxOutputTokens: options.maxTokens || 1000,
                    temperature: options.temperature,
                    topP: options.topP,
                }
            }, this._geminiRequestOptions(options));

            const result = await model.generateContentStream(messages.user);

            let fullText = '';
            let inputTokens = 0;
            let outputTokens = 0;

            // Process stream
            for await (const chunk of result.stream) {
                try {
                    const text = chunk.text();
                    if (text) {
                        fullText += text;

                        // Call partial callback if provided
                        if (options.partial) {
                            options.partial(text);
                        }
                    }
                } catch (chunkError) {
                    // Some chunks may not have text (e.g., thinking tokens)
                    // Continue processing other chunks
                }
                // Capture usage from final chunk
                if (chunk.usageMetadata) {
                    inputTokens = chunk.usageMetadata.promptTokenCount || 0;
                    outputTokens = chunk.usageMetadata.candidatesTokenCount || 0;
                }
            }

            const durationMs = Date.now() - startTime;

            // Estimate tokens if not provided
            if (inputTokens === 0) {
                inputTokens = Math.ceil((systemPrompt.length + messages.user.length) / 4);
            }
            if (outputTokens === 0) {
                outputTokens = Math.ceil(fullText.length / 4);
            }

            llmUsageLogger.log({
                timestamp: new Date().toISOString(),
                requestId,
                provider: 'gemini',
                model: modelName,
                method: 'stream',
                inputTokens,
                outputTokens,
                totalTokens: inputTokens + outputTokens,
                costUSD: llmUsageLogger.calculateCost(modelName, inputTokens, outputTokens),
                durationMs,
                success: true,
            });

            return fullText;
        } catch (error) {
            const durationMs = Date.now() - startTime;
            llmUsageLogger.log({
                timestamp: new Date().toISOString(),
                requestId,
                provider: 'gemini',
                model: modelName,
                method: 'stream',
                inputTokens: 0,
                outputTokens: 0,
                totalTokens: 0,
                costUSD: 0,
                durationMs,
                success: false,
                error: error instanceof Error ? error.message : String(error),
            });
            throw error;
        }
    }

    /**
     * Recursively strip unsupported JSON Schema properties for Gemini
     * Gemini doesn't support: additionalProperties, $schema, etc.
     */
    private static _cleanSchemaForGemini(obj: any): any {
        if (obj === null || typeof obj !== 'object') {
            return obj;
        }

        if (Array.isArray(obj)) {
            return obj.map(item => this._cleanSchemaForGemini(item));
        }

        const cleaned: any = {};
        for (const [key, value] of Object.entries(obj)) {
            // Skip properties that Gemini doesn't support
            if (key === 'additionalProperties' || key === '$schema') {
                continue;
            }
            cleaned[key] = this._cleanSchemaForGemini(value);
        }
        return cleaned;
    }

    private static async _geminiStreamWithTools(
        messages: LLMMessages,
        tools: Tool[],
        toolHandler: (toolName: string, toolInput: any) => Promise<any>,
        modelName: string,
        options: LLMOptions,
        maxIterations: number
    ): Promise<string> {
        const methodStartTime = Date.now();
        const apiKey = options.apiKey || process.env.GEMINI_API_KEY || "";
        const genAI = new GoogleGenerativeAI(apiKey);

        // Convert system prompt to string if it's an array
        const systemPrompt = typeof messages.sys === 'string'
            ? messages.sys
            : messages.sys.map(block => block.text).join('\n');

        // Convert tools to Gemini function declarations format
        // Clean schemas to remove unsupported properties (additionalProperties, $schema, etc.)
        const functionDeclarations = tools.map(tool => ({
            name: tool.name,
            description: tool.description,
            parameters: {
                type: SchemaType.OBJECT,
                properties: this._cleanSchemaForGemini(tool.input_schema.properties) as { [key: string]: FunctionDeclarationSchemaProperty },
                required: tool.input_schema.required || []
            }
        }));

        const model = genAI.getGenerativeModel({
            model: modelName,
            systemInstruction: systemPrompt,
            tools: [{ functionDeclarations }],
            generationConfig: {
                maxOutputTokens: options.maxTokens || 4000,
                temperature: options.temperature,
                topP: options.topP,
            }
        }, this._geminiRequestOptions(options));

        // Start a chat session to maintain conversation history for tool calling loop
        const chat = model.startChat({
            history: []
        });

        let iterations = 0;
        let finalText = '';
        let currentUserMessage = messages.user;
        let totalToolCalls = 0;

        // Track cumulative usage across all iterations
        let totalInputTokens = 0;
        let totalOutputTokens = 0;

        while (iterations < maxIterations) {
            // Abort checkpoint: stop before starting a fresh LLM turn (mirrors the
            // Anthropic/OpenAI paths).
            if (options.signal?.aborted) throw new RequestAbortedError();

            iterations++;
            const iterationStartTime = Date.now();
            const requestId = llmUsageLogger.generateRequestId();

            // Send message and stream the response
            const result = await chat.sendMessageStream(currentUserMessage);

            let responseText = '';
            const functionCalls: Array<{ name: string; args: any }> = [];
            let inputTokens = 0;
            let outputTokens = 0;

            // Process the stream
            for await (const chunk of result.stream) {
                const candidate = chunk.candidates?.[0];
                if (!candidate) continue;

                for (const part of candidate.content?.parts || []) {
                    if (part.text) {
                        responseText += part.text;
                        // Stream text chunks to callback
                        if (options.partial) {
                            options.partial(part.text);
                        }
                    } else if (part.functionCall) {
                        functionCalls.push({
                            name: part.functionCall.name,
                            args: part.functionCall.args
                        });
                    }
                }

                // Capture usage from chunks
                if (chunk.usageMetadata) {
                    inputTokens = chunk.usageMetadata.promptTokenCount || 0;
                    outputTokens = chunk.usageMetadata.candidatesTokenCount || 0;
                }
            }

            const iterationDuration = Date.now() - iterationStartTime;
            const toolCallsInIteration = functionCalls.length;
            totalToolCalls += toolCallsInIteration;

            // Estimate tokens if not provided by API
            if (inputTokens === 0) {
                const userMsg = typeof currentUserMessage === 'string' ? currentUserMessage : JSON.stringify(currentUserMessage);
                inputTokens = Math.ceil((systemPrompt.length + userMsg.length) / 4);
            }
            if (outputTokens === 0) {
                outputTokens = Math.ceil(responseText.length / 4);
            }

            // Update cumulative totals
            totalInputTokens += inputTokens;
            totalOutputTokens += outputTokens;

            // Log this iteration
            llmUsageLogger.log({
                timestamp: new Date().toISOString(),
                requestId,
                provider: 'gemini',
                model: modelName,
                method: `streamWithTools[iter=${iterations}]`,
                inputTokens,
                outputTokens,
                totalTokens: inputTokens + outputTokens,
                costUSD: llmUsageLogger.calculateCost(modelName, inputTokens, outputTokens),
                durationMs: iterationDuration,
                toolCalls: toolCallsInIteration,
                success: true,
            });

            // If no function calls, we're done
            if (functionCalls.length === 0) {
                finalText = responseText;
                break;
            }

            // Execute all function calls and collect results
            const functionResponses: Array<{ name: string; response: any }> = [];

            // Execute all function calls in parallel for lower latency
            const responses = await Promise.all(functionCalls.map(async (fc) => {
                try {
                    const result = await toolHandler(fc.name, fc.args);
                    // Truncate very large results to reduce memory and token usage
                    let resultContent = typeof result === 'string' ? result : JSON.stringify(result);
                    const MAX_RESULT_LENGTH = 50000; // ~12k tokens max per tool result
                    if (resultContent.length > MAX_RESULT_LENGTH) {
                        // Surrogate-aware cut so the truncation can't split a UTF-16 pair.
                        resultContent = safeTruncate(resultContent, MAX_RESULT_LENGTH) +
                            '\n\n... [Result truncated - showing first 50000 characters of ' + resultContent.length + ' total]';
                    }
                    return {
                        name: fc.name,
                        // Final safety net: strip lone surrogates from source-data results.
                        response: { result: stripLoneSurrogates(resultContent) }
                    };
                } catch (error) {
                    return {
                        name: fc.name,
                        response: { error: error instanceof Error ? error.message : String(error) }
                    };
                }
            }));
            functionResponses.push(...responses);

            // Send function responses back to the model
            // Build the function response parts for the next message
            const functionResponseParts = functionResponses.map(fr => ({
                functionResponse: {
                    name: fr.name,
                    response: fr.response
                }
            }));

            // Send function responses as the next user message
            currentUserMessage = functionResponseParts as any;
        }

        // Log total summary for all iterations
        const totalDuration = Date.now() - methodStartTime;
        if (iterations > 1) {
            llmUsageLogger.log({
                timestamp: new Date().toISOString(),
                requestId: llmUsageLogger.generateRequestId(),
                provider: 'gemini',
                model: modelName,
                method: `streamWithTools[TOTAL:${iterations}iters]`,
                inputTokens: totalInputTokens,
                outputTokens: totalOutputTokens,
                totalTokens: totalInputTokens + totalOutputTokens,
                costUSD: llmUsageLogger.calculateCost(modelName, totalInputTokens, totalOutputTokens),
                durationMs: totalDuration,
                toolCalls: totalToolCalls,
                success: true,
            });
        }

        if (iterations >= maxIterations) {
            throw new Error(`Max iterations (${maxIterations}) reached in tool calling loop`);
        }

        return finalText;
    }

    // ============================================================
    // OPENAI IMPLEMENTATION (also serves OpenRouter via the wrappers above)

    /** True for Anthropic/Claude model ids — gates OpenRouter prompt caching. */
    private static _isClaudeModel(model: string): boolean {
        return /claude/i.test(model || '');
    }

    /**
     * Build the OpenAI-wire system message. For OpenRouter + Claude
     * (cacheClaude=true) it emits content parts carrying Anthropic
     * `cache_control` breakpoints (preserving any the caller set, else marking
     * the last block), so OpenRouter forwards them to Anthropic for prompt
     * caching. Otherwise it returns a plain flattened string — unchanged for
     * direct OpenAI/Groq.
     */
    private static _openaiSystemMessage(
        sys: SystemPrompt,
        flattened: string,
        cacheClaude: boolean
    ): OpenAI.Chat.Completions.ChatCompletionMessageParam {
        if (!cacheClaude) {
            return { role: 'system', content: flattened };
        }
        const parts: any[] = typeof sys === 'string'
            ? [{ type: 'text', text: sys, cache_control: { type: 'ephemeral' } }]
            : sys.map(b => ({
                type: 'text',
                text: b.text,
                ...((b as any).cache_control ? { cache_control: (b as any).cache_control } : {}),
            }));
        if (parts.length > 0 && !parts.some(p => p.cache_control)) {
            parts[parts.length - 1].cache_control = { type: 'ephemeral' };
        }
        return { role: 'system', content: parts as any };
    }

    /**
     * Split an OpenAI-wire usage object. `prompt_tokens` INCLUDES cached tokens,
     * so we subtract them out (Anthropic-style: input excludes cache reads) and
     * report cached separately — this makes calculateCost price cache reads at
     * the discounted rate and reflects OpenRouter prompt-cache savings in logs.
     */
    private static _openaiUsage(u: any): { inputTokens: number; outputTokens: number; cacheReadTokens: number } {
        const cached = u?.prompt_tokens_details?.cached_tokens || 0;
        return {
            inputTokens: Math.max(0, (u?.prompt_tokens || 0) - cached),
            outputTokens: u?.completion_tokens || 0,
            cacheReadTokens: cached,
        };
    }

    // ============================================================

    private static async _openaiText(
        messages: LLMMessages,
        modelName: string,
        options: LLMOptions
    ): Promise<string> {
        const startTime = Date.now();
        const requestId = llmUsageLogger.generateRequestId();
        const apiKey = options.apiKey || process.env.OPENAI_API_KEY || "";
        const openai = new OpenAI({ apiKey, baseURL: this._proxyBaseURL('openai', options) });

        // Convert system prompt to string if it's an array
        const systemPrompt = typeof messages.sys === 'string'
            ? messages.sys
            : messages.sys.map(block => block.text).join('\n');

        try {
            if (options.signal?.aborted) throw new RequestAbortedError();
            const response = await openai.chat.completions.create({
                model: modelName,
                ...((options._openrouterProvider ? { provider: options._openrouterProvider } : {}) as any),
                messages: [
                    this._openaiSystemMessage(messages.sys, systemPrompt, !!options._openrouterClaudeCaching),
                    { role: 'user', content: messages.user }
                ],
                max_tokens: options.maxTokens || 1000,
                temperature: options.temperature,
                top_p: options.topP,
            }, { signal: options.signal });

            const durationMs = Date.now() - startTime;
            const usage = response.usage;
            const inputTokens = usage?.prompt_tokens || 0;
            const outputTokens = usage?.completion_tokens || 0;

            llmUsageLogger.log({
                timestamp: new Date().toISOString(),
                requestId,
                provider: 'openai',
                model: modelName,
                method: 'text',
                inputTokens,
                outputTokens,
                totalTokens: inputTokens + outputTokens,
                costUSD: llmUsageLogger.calculateCost(modelName, inputTokens, outputTokens),
                durationMs,
                success: true,
            });

            return response.choices[0]?.message?.content || '';
        } catch (error) {
            const durationMs = Date.now() - startTime;
            llmUsageLogger.log({
                timestamp: new Date().toISOString(),
                requestId,
                provider: 'openai',
                model: modelName,
                method: 'text',
                inputTokens: 0,
                outputTokens: 0,
                totalTokens: 0,
                costUSD: 0,
                durationMs,
                success: false,
                error: error instanceof Error ? error.message : String(error),
            });
            throw error;
        }
    }

    private static async _openaiStream(
        messages: LLMMessages,
        modelName: string,
        options: LLMOptions,
        json?: boolean
    ): Promise<any> {
        const startTime = Date.now();
        const requestId = llmUsageLogger.generateRequestId();
        const apiKey = options.apiKey || process.env.OPENAI_API_KEY || "";
        const openai = new OpenAI({ apiKey, baseURL: this._proxyBaseURL('openai', options) });

        // Convert system prompt to string if it's an array
        const systemPrompt = typeof messages.sys === 'string'
            ? messages.sys
            : messages.sys.map(block => block.text).join('\n');

        try {
            const createParams: OpenAI.Chat.Completions.ChatCompletionCreateParamsStreaming = {
                model: modelName,
                messages: [
                    this._openaiSystemMessage(messages.sys, systemPrompt, !!options._openrouterClaudeCaching),
                    { role: 'user', content: messages.user }
                ],
                max_tokens: options.maxTokens || 1000,
                temperature: options.temperature,
                top_p: options.topP,
                response_format: json ? { type: 'json_object' } : undefined,
                stream: true,
                stream_options: { include_usage: true }, // Request usage info in stream
            };
            // OpenRouter-only routing hint (extra body field; doesn't affect typing)
            if (options._openrouterProvider) (createParams as unknown as Record<string, unknown>).provider = options._openrouterProvider;
            if (options.signal?.aborted) throw new RequestAbortedError();
            const stream = await openai.chat.completions.create(createParams, { signal: options.signal });

            let fullText = '';
            let inputTokens = 0;
            let outputTokens = 0;
            let cacheReadTokens = 0;

            for await (const chunk of stream) {
                const content = chunk.choices[0]?.delta?.content || '';
                if (content) {
                    fullText += content;

                    // Call partial callback if provided
                    if (options.partial) {
                        options.partial(content);
                    }
                }
                // OpenAI provides usage in the final chunk when stream_options.include_usage is true
                if (chunk.usage) {
                    const u = this._openaiUsage(chunk.usage);
                    inputTokens = u.inputTokens;
                    outputTokens = u.outputTokens;
                    cacheReadTokens = u.cacheReadTokens;
                }
            }

            const durationMs = Date.now() - startTime;

            // Estimate tokens if not provided
            if (inputTokens === 0) {
                inputTokens = Math.ceil((systemPrompt.length + messages.user.length) / 4);
            }
            if (outputTokens === 0) {
                outputTokens = Math.ceil(fullText.length / 4);
            }

            llmUsageLogger.log({
                timestamp: new Date().toISOString(),
                requestId,
                provider: 'openai',
                model: modelName,
                method: 'stream',
                inputTokens,
                outputTokens,
                cacheReadTokens,
                totalTokens: inputTokens + outputTokens + cacheReadTokens,
                costUSD: llmUsageLogger.calculateCost(modelName, inputTokens, outputTokens, cacheReadTokens),
                durationMs,
                success: true,
            });

            // Return parsed JSON or text
            if (json) {
                return this._parseJSON(fullText);
            }

            return fullText;
        } catch (error) {
            const durationMs = Date.now() - startTime;
            llmUsageLogger.log({
                timestamp: new Date().toISOString(),
                requestId,
                provider: 'openai',
                model: modelName,
                method: 'stream',
                inputTokens: 0,
                outputTokens: 0,
                totalTokens: 0,
                costUSD: 0,
                durationMs,
                success: false,
                error: error instanceof Error ? error.message : String(error),
            });
            throw error;
        }
    }

    /** Map the Anthropic-style firstIterationToolChoice to OpenAI's tool_choice. */
    private static _openaiToolChoice(
        choice: { type: 'any' } | { type: 'tool'; name: string }
    ): OpenAI.Chat.Completions.ChatCompletionToolChoiceOption {
        return choice.type === 'any'
            ? 'required'
            : { type: 'function', function: { name: choice.name } };
    }

    private static async _openaiStreamWithTools(
        messages: LLMMessages,
        tools: Tool[],
        toolHandler: (toolName: string, toolInput: any) => Promise<any>,
        modelName: string,
        options: LLMOptions,
        maxIterations: number
    ): Promise<string> {
        const apiKey = options.apiKey || process.env.OPENAI_API_KEY || "";
        const openai = new OpenAI({ apiKey, baseURL: this._proxyBaseURL('openai', options) });

        // Convert system prompt to string if it's an array
        const systemPrompt = typeof messages.sys === 'string'
            ? messages.sys
            : messages.sys.map(block => block.text).join('\n');

        // Convert tools to OpenAI function format
        const openaiTools: OpenAI.Chat.Completions.ChatCompletionTool[] = tools.map(tool => ({
            type: 'function' as const,
            function: {
                name: tool.name,
                description: tool.description,
                parameters: {
                    type: tool.input_schema.type,
                    properties: tool.input_schema.properties,
                    required: tool.input_schema.required || []
                }
            }
        }));

        // Build conversation messages
        const conversationMessages: OpenAI.Chat.Completions.ChatCompletionMessageParam[] = [
            this._openaiSystemMessage(messages.sys, systemPrompt, !!options._openrouterClaudeCaching),
            { role: 'user', content: messages.user }
        ];

        let iterations = 0;
        let finalText = '';

        while (iterations < maxIterations) {
            // Abort checkpoint: stop before starting a fresh LLM turn (mirrors the
            // Anthropic path). Cancellation between turns short-circuits here.
            if (options.signal?.aborted) throw new RequestAbortedError();

            iterations++;
            const iterStart = Date.now();
            const requestId = llmUsageLogger.generateRequestId();
            let inputTokens = 0, outputTokens = 0, cacheReadTokens = 0;

            // Force a tool call on the first iteration only (fork mode), mirroring
            // the Anthropic path. After iter 1 we revert to auto. See
            // firstIterationToolChoice.
            const toolChoice = (iterations === 1 && options.firstIterationToolChoice)
                ? this._openaiToolChoice(options.firstIterationToolChoice)
                : undefined;

            const createParams: OpenAI.Chat.Completions.ChatCompletionCreateParamsStreaming = {
                model: modelName,
                messages: conversationMessages,
                max_tokens: options.maxTokens || 4000,
                temperature: options.temperature,
                top_p: options.topP,
                tools: openaiTools,
                ...(toolChoice ? { tool_choice: toolChoice } : {}),
                stream: true,
                stream_options: { include_usage: true },
            };
            // OpenRouter-only routing hint (extra body field; doesn't affect typing)
            if (options._openrouterProvider) (createParams as unknown as Record<string, unknown>).provider = options._openrouterProvider;
            const stream = await openai.chat.completions.create(createParams, { signal: options.signal });

            let responseText = '';
            const toolCalls: Array<{
                id: string;
                name: string;
                arguments: string;
            }> = [];

            // Track tool call assembly (OpenAI streams tool calls in chunks)
            const toolCallsInProgress: Map<number, { id: string; name: string; arguments: string }> = new Map();

            for await (const chunk of stream) {
                const delta = chunk.choices[0]?.delta;

                // Usage arrives in the final chunk (stream_options.include_usage)
                if (chunk.usage) {
                    const u = this._openaiUsage(chunk.usage);
                    inputTokens = u.inputTokens;
                    outputTokens = u.outputTokens;
                    cacheReadTokens = u.cacheReadTokens;
                }

                // Handle text content
                if (delta?.content) {
                    responseText += delta.content;
                    if (options.partial) {
                        options.partial(delta.content);
                    }
                }

                // Handle tool calls
                if (delta?.tool_calls) {
                    for (const toolCallDelta of delta.tool_calls) {
                        const index = toolCallDelta.index;

                        if (!toolCallsInProgress.has(index)) {
                            toolCallsInProgress.set(index, {
                                id: toolCallDelta.id || '',
                                name: toolCallDelta.function?.name || '',
                                arguments: ''
                            });
                        }

                        const tc = toolCallsInProgress.get(index)!;

                        if (toolCallDelta.id) {
                            tc.id = toolCallDelta.id;
                        }
                        if (toolCallDelta.function?.name) {
                            tc.name = toolCallDelta.function.name;
                        }
                        if (toolCallDelta.function?.arguments) {
                            tc.arguments += toolCallDelta.function.arguments;
                        }
                    }
                }
            }

            // Collect completed tool calls
            for (const tc of toolCallsInProgress.values()) {
                if (tc.id && tc.name) {
                    toolCalls.push(tc);
                }
            }

            // Log this iteration's usage (estimate if the provider omitted it)
            if (inputTokens === 0) inputTokens = Math.ceil(systemPrompt.length / 4);
            if (outputTokens === 0) outputTokens = Math.ceil(responseText.length / 4);
            llmUsageLogger.log({
                timestamp: new Date().toISOString(),
                requestId,
                provider: 'openai',
                model: modelName,
                method: `streamWithTools[iter=${iterations}]`,
                inputTokens,
                outputTokens,
                cacheReadTokens,
                totalTokens: inputTokens + outputTokens + cacheReadTokens,
                costUSD: llmUsageLogger.calculateCost(modelName, inputTokens, outputTokens, cacheReadTokens),
                durationMs: Date.now() - iterStart,
                toolCalls: toolCalls.length,
                success: true,
            });

            // If no tool calls, we're done
            if (toolCalls.length === 0) {
                finalText = responseText;
                break;
            }

            // Add assistant message with tool calls to conversation
            conversationMessages.push({
                role: 'assistant',
                content: responseText || null,
                tool_calls: toolCalls.map(tc => ({
                    id: tc.id,
                    type: 'function' as const,
                    function: {
                        name: tc.name,
                        arguments: tc.arguments
                    }
                }))
            });

            // Execute all tool calls in parallel for lower latency
            const toolCallResults = await Promise.all(toolCalls.map(async (tc) => {
                let result: string;
                try {
                    const args = JSON.parse(tc.arguments);
                    const toolResult = await toolHandler(tc.name, args);
                    result = typeof toolResult === 'string' ? toolResult : JSON.stringify(toolResult);
                    // Truncate very large results to reduce memory and token usage
                    const MAX_RESULT_LENGTH = 50000; // ~12k tokens max per tool result
                    if (result.length > MAX_RESULT_LENGTH) {
                        // Surrogate-aware cut so the truncation can't split a UTF-16 pair.
                        result = safeTruncate(result, MAX_RESULT_LENGTH) +
                            '\n\n... [Result truncated - showing first 50000 characters of ' + result.length + ' total]';
                    }
                } catch (error) {
                    result = JSON.stringify({ error: error instanceof Error ? error.message : String(error) });
                }
                // Final safety net: strip lone surrogates from source-data results.
                return { role: 'tool' as const, tool_call_id: tc.id, content: stripLoneSurrogates(result) };
            }));
            toolCallResults.forEach(r => conversationMessages.push(r as any));
        }

        if (iterations >= maxIterations) {
            throw new Error(`Max iterations (${maxIterations}) reached in tool calling loop`);
        }

        return finalText;
    }

    // ============================================================
    // JSON PARSING HELPER
    // ============================================================

    /**
     * Parse JSON string, handling markdown code blocks and surrounding text
     * Enhanced version with jsonrepair to handle malformed JSON from LLMs
     * @param text - Text that may contain JSON wrapped in ```json...``` or with surrounding text
     * @returns Parsed JSON object or array
     */
    private static _parseJSON(text: string): any {
        let jsonText = text.trim();

        // Step 1: Remove markdown code blocks (jsonrepair doesn't handle markdown)
        if (jsonText.startsWith('```json')) {
            jsonText = jsonText.replace(/^```json\s*\n?/, '').replace(/\n?```\s*$/, '');
        } else if (jsonText.startsWith('```')) {
            jsonText = jsonText.replace(/^```\s*\n?/, '').replace(/\n?```\s*$/, '');
        }

        // Step 2: Extract JSON from surrounding text using proper brace matching
        // Find the first JSON object or array and extract only that
        const firstBrace = jsonText.indexOf('{');
        const firstBracket = jsonText.indexOf('[');

        // Determine if it's an object or array
        let startIdx = -1;
        let openChar = '';
        let closeChar = '';

        if (firstBrace !== -1 && (firstBracket === -1 || firstBrace < firstBracket)) {
            // Object starts first
            startIdx = firstBrace;
            openChar = '{';
            closeChar = '}';
        } else if (firstBracket !== -1) {
            // Array starts first
            startIdx = firstBracket;
            openChar = '[';
            closeChar = ']';
        }

        // If no JSON structure found at all, throw a clear error
        if (startIdx === -1) {
            const error = new Error(`No JSON found in response. LLM returned plain text instead of JSON.`);
            userPromptErrorLogger.logJsonParseError('LLM._parseJSON - No JSON structure found', text, error);
            throw error;
        }

        // Find the MATCHING closing brace/bracket (not the last one in the document)
        let depth = 0;
        let inString = false;
        let endIdx = -1;

        for (let i = startIdx; i < jsonText.length; i++) {
            const char = jsonText[i];
            const prevChar = i > 0 ? jsonText[i - 1] : '';

            // Handle string literals (skip content inside quotes)
            if (char === '"' && prevChar !== '\\') {
                inString = !inString;
                continue;
            }

            if (!inString) {
                if (char === openChar) {
                    depth++;
                } else if (char === closeChar) {
                    depth--;
                    if (depth === 0) {
                        endIdx = i;
                        break;
                    }
                }
            }
        }

        if (endIdx !== -1) {
            jsonText = jsonText.substring(startIdx, endIdx + 1);
        } else {
            // No matching closing brace found
            const error = new Error(`Incomplete JSON - no matching closing ${closeChar} found.`);
            userPromptErrorLogger.logJsonParseError('LLM._parseJSON - Incomplete JSON', text, error);
            throw error;
        }

        // Step 3: Use jsonrepair to fix malformed JSON before parsing
        // This handles: missing commas, trailing commas, incomplete JSON, improper escaping, etc.
        try {
            const repairedJson = jsonrepair(jsonText);
            return JSON.parse(repairedJson);
        } catch (error) {
            // If jsonrepair fails, log the full raw string and throw
            const parseError = error instanceof Error ? error : new Error(String(error));
            userPromptErrorLogger.logJsonParseError('LLM._parseJSON - JSON parse/repair failed', text, parseError);
            throw new Error(`Failed to parse JSON: ${parseError.message}`);
        }
    }
}